import React, { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from "uuid";
import Grid from '@mui/material/Grid2';
import {  
  Feature,
  FeatureBase,
} from "@models";
import {  
  Summon,
  UpgradableNumber,
  TemplateBase, 
  Creature,
  AbilityScoresUpgradable,
  SENSES,
} from "@dndModels";
import {
  CREATURE_FEATURE_TYPES,
  CREATURE_TYPES,
  SIZE_OPTIONS
} from "@dndModels/Constants";
import { CreatureType, isSizeType } from '@dndModels/Types';

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import LoadingBox from "@components/input/LoadingBox";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import CreatureAbilityScoresUpgradableInput from "@dndComponents/creature/CreatureAbilityScoresUpgradableInput";
import FeatureListInput from "@dndComponents/feature/FeatureListInput";
import FeatureInput from "@dndComponents/feature/FeatureInput";

import TemplateBox from "@dndComponents/TemplateBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import StringsBox from "@components/input/strings/StringsBox";
import CheckBox from '@components/input/CheckBox';
import AttributesInput from '@dndComponents/creature/AttributesInput';
import SelectObjectBox from '@components/input/selects/SelectObjectBox';
import FeatureBaseInput from './FeatureBaseInput';
import FeatureBasesInput from './FeatureBasesInput';

interface Props { 
  obj: Summon;
  onChange: (changed: Summon) => void;
  slot_level: number;
  disabled: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SummonStatBlockInput({ 
  obj,
  onChange,
  slot_level,
  disabled
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  // const [tools, setTools] = useState<Tool[]>([]);
  // const [conditions, setConditions] = useState<Condition[]>([]);
  // const [senses, setSenses] = useState<Sense[]>([]);
  const [mode, setMode] = useState("Home");
  const [ping, setPing] = useState(false);
  const [expanded_feature_base, setExpandedFeatureBase] = useState<FeatureBase | null>(null);
  // const [edit_attribute, setEditAttribute] = useState("");

  const load = useCallback( async () => {
    // const res = await api.getSetOfObjects({ data_types: ["tool","condition"]});
    // setTools(res.tool as Tool[]);
    // setConditions(res.condition as Condition[]);
    const new_senses = SENSES;
    // setSenses(new_senses);
    const my_senses = Object.keys(obj.senses);
    if (!common_util.list1ContainsList2(new_senses.map(o => o.name), my_senses)) {
      const new_obj = obj;
      for (const sense of my_senses) {
        if (new_senses.filter(o => o.name === sense).length === 0) {
          delete new_obj.senses[sense];
        }
      }
      onChange(new_obj);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const copyCreature = async (copyMe: Creature) => {
    const new_obj = obj;
    new_obj.name = copyMe.name;
    new_obj.description = copyMe.description;
    new_obj.creature_type = copyMe.creature_type;
    new_obj.swarm = copyMe.swarm;
    new_obj.subtypes = [...copyMe.subtypes];
    new_obj.tags = [...copyMe.tags];
    new_obj.challenge_rating = new UpgradableNumber();
    new_obj.challenge_rating.base = copyMe.challenge_rating;
    new_obj.image_url = copyMe.image_url;
    new_obj.ability_scores = copyMe.ability_scores.clone();
    new_obj.fbs = copyMe.fbs.map(o => o.clone(true));
    // new_obj.actions = [];
    // for (const fb of copyMe.action_fbs) {
    //   for (const f of fb.features) {
    //     if (f.floating_feature instanceof CreatureAbility) {
    //       const clone = f.clone(true);
    //       const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
    //       if (ff_finder.length === 1) {
    //         const floating_feature = ff_finder[0];
    //         clone.floating_feature_id = floating_feature._id;
    //         clone.floating_feature = floating_feature;
    //       } else {
    //         const floating_feature = new MinionAbility();
    //         floating_feature.copyCreatureAbility(f.floating_feature);
    //         const res = await api.upsertObject(floating_feature.data_type, floating_feature);
    //         if (res) {
    //           floating_feature._id = res.body.id;
    //           api.add_floating_feature(floating_feature);
    //           clone.floating_feature_id = floating_feature._id;
    //           clone.floating_feature = floating_feature;
    //         }
    //       }
    //       new_obj.actions.push(clone);
    //     } else {
    //       new_obj.actions.push(f.clone(true));
    //     }
    //   }
    // }
    // new_obj.legendary_actions = [];
    // for (const fb of copyMe.legendary_action_fbs) {
    //   for (const f of fb.features) {
    //     if (f.floating_feature instanceof CreatureAbility) {
    //       const clone = f.clone(true);
    //       const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
    //       if (ff_finder.length === 1) {
    //         const floating_feature = ff_finder[0];
    //         clone.floating_feature_id = floating_feature._id;
    //         clone.floating_feature = floating_feature;
    //       } else {
    //         const floating_feature = new MinionAbility();
    //         floating_feature.copyCreatureAbility(f.floating_feature);
    //         const res = await api.upsertObject(floating_feature.data_type, floating_feature);
    //         if (res) {
    //           floating_feature._id = res.body.id;
    //           api.add_floating_feature(floating_feature);
    //           clone.floating_feature_id = floating_feature._id;
    //           clone.floating_feature = floating_feature;
    //         }
    //       }
    //       new_obj.legendary_actions.push(clone);
    //     } else {
    //       new_obj.legendary_actions.push(f.clone(true));
    //     }
    //   }
    // }
    // new_obj.special_abilities = [];
    // for (const fb of copyMe.traits) {
    //   for (const f of fb.features) {
    //     if (f.floating_feature instanceof CreatureAbility) {
    //       const clone = f.clone(true);
    //       const ff_finder = api.all_floating_features.filter(o2 => o2 instanceof MinionAbility && o2.name === f.name && o2.description === f.description);
    //       if (ff_finder.length === 1) {
    //         const floating_feature = ff_finder[0];
    //         clone.floating_feature_id = floating_feature._id;
    //         clone.floating_feature = floating_feature;
    //       } else {
    //         const floating_feature = new MinionAbility();
    //         floating_feature.copyCreatureAbility(f.floating_feature);
    //         const res = await api.upsertObject(floating_feature.data_type, floating_feature);
    //         if (res) {
    //           floating_feature._id = res.body.id;
    //           api.add_floating_feature(floating_feature);
    //           clone.floating_feature_id = floating_feature._id;
    //           clone.floating_feature = floating_feature;
    //         }
    //       }
    //       new_obj.special_abilities.push(clone);
    //     } else {
    //       new_obj.special_abilities.push(f.clone(true));
    //     }
    //   }
    // }
    new_obj.hd_size_from_size = true;
    // new_obj.hit_dice_size = new UpgradableNumber();
    new_obj.hit_dice_count = new UpgradableNumber();
    if (copyMe.hit_dice.length > 0) {
      // new_obj.hit_dice_size.base = copyMe.hit_dice[0].size;
      new_obj.hit_dice_count = copyMe.hit_dice[0].count.clone();
    }
    new_obj.max_hit_points = new UpgradableNumber();
    const attributes = copyMe.attributes_table.getEntry(copyMe.challenge_rating);
    if (attributes) {
      new_obj.attributes = attributes.clone();
    }
    new_obj.initiative_modifier = copyMe.initiative_modifier.clone();
    new_obj.armor_class = copyMe.armor_class.clone();
    new_obj.size_num = copyMe.size_num_for_cr(copyMe.challenge_rating);
    new_obj.alignment = copyMe.alignment;
    new_obj.connected = false;
    return new_obj;
  }

  const render_tab = () => {
    if (mode === "Ability Scores") {
      return (
        <CreatureAbilityScoresUpgradableInput
          obj={obj}
          ability_scores={obj.ability_scores}
          disabled={disabled}
          onChange={(changed: AbilityScoresUpgradable) => {
            const new_obj = obj;
            new_obj.ability_scores = changed;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    } else if (mode === "Attributes") {
      return (
        <AttributesInput
          attributes={obj.attributes}
          disabled={disabled}
          onChange={() => { // I should really change this input so it gives the changed version, and keeps a clone on it, but that's low priority.
            const new_obj = obj;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    } else if (mode === "Actions") {
      return renderActions();
      // if (expanded_feature) {
      //   return (
      //     <FeatureInput
      //       label="Action"
      //       slot_level={slot_level}
      //       parent_name={obj.name}
      //       base_name={null}
      //       minion
      //       feature={expanded_feature} 
      //       disabled={disabled}
      //       allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
      //       onChange={(changed: Feature) => {
      //         const new_obj = obj;
      //         new_obj.actions = common_util.replaceInList(obj.actions, changed.true_id, changed, "true_id");
      //         onChange(new_obj);
      //         setPing(!ping);
      //       }}
      //       onDelete={() => {
      //         if (expanded_feature) {
      //           const id = expanded_feature.true_id;
      //           const new_obj = obj;
      //           const features = new_obj.actions.filter(o => o.true_id !== id);
      //           new_obj.actions = features;
      //           setExpandedFeature(null);
      //           onChange(new_obj);
      //           setPing(!ping);
      //         }
      //       }}
      //       onDone={() => {
      //         setExpandedFeature(null);
      //       }}
      //     />
      //   );
      // } else {
      //   return (
      //     <FeatureListInput 
      //       label="Action"
      //       features={obj.actions} 
      //       parent_id={obj.true_id} 
      //       parent_type="SummonStatBlock"
      //       disabled={disabled}
      //       onExpand={setExpandedFeature}
      //       onAdd={() => {
      //         const new_obj = obj;
      //         const feature = new Feature();
      //         // feature.parent_type = "SummonStatBlock";
      //         // feature.parent_id = obj.true_id;
      //         // feature.id = obj.actions.length;
      //         new_obj.actions.push(feature);
      //         setExpandedFeature(feature);
      //         onChange(new_obj);
      //         setPing(!ping);
      //       }}
      //     />
      //   );
      // }
    // } else if (mode === "Legendary Actions") {
    //   return renderLegendaryActions();
    //   // if (expanded_feature) {
    //   //   return (
    //   //     <FeatureInput
    //   //       label="Legendary Action"
    //   //       slot_level={slot_level}
    //   //       parent_name={obj.name}
    //   //       base_name={null}
    //   //       minion
    //   //       feature={expanded_feature} 
    //   //       disabled={disabled}
    //   //       allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
    //   //       onChange={(changed: Feature) => {
    //   //         const new_obj = obj;
    //   //         new_obj.legendary_actions = common_util.replaceInList(new_obj.legendary_actions, changed.true_id, changed, "true_id");
              
    //   //         onChange(new_obj);
    //   //         setPing(!ping);
    //   //       }}
    //   //       onDelete={() => {
    //   //         if (expanded_feature) {
    //   //           const id = expanded_feature.true_id;
    //   //           const new_obj = obj;
    //   //           const features = new_obj.legendary_actions.filter(o => o.true_id !== id);
    //   //           new_obj.legendary_actions = features;
                
    //   //           setExpandedFeature(null);
    //   //           onChange(new_obj);
    //   //           setPing(!ping);
    //   //         }
    //   //       }}
    //   //       onDone={() => {
    //   //         setExpandedFeature(null);
    //   //       }}
    //   //     />
    //   //   );
    //   // } else {
    //   //   return (
    //   //     <FeatureListInput 
    //   //       label="Legendary Action"
    //   //       features={obj.legendary_actions} 
    //   //       parent_id={obj.true_id} 
    //   //       parent_type="SummonStatBlock"
    //   //       disabled={disabled}
    //   //       onExpand={setExpandedFeature}
    //   //       onAdd={() => {
    //   //         const new_obj = obj;
    //   //         const feature = new Feature();
    //   //         // feature.parent_type = "SummonStatBlock";
    //   //         // feature.parent_id = obj.true_id;
    //   //         new_obj.legendary_actions.push(feature);
    //   //         setExpandedFeature(null);
    //   //         onChange(new_obj);
    //   //         setPing(!ping);
    //   //       }}
    //   //     />
    //   //   );
    //   // }
    // } else if (mode === "Lair Actions") {
    //   return renderLairActions();
    } else if (mode === "Traits") {
      return renderTraits();
      // if (expanded_feature) {
      //   return (
      //     <FeatureInput
      //       label="Trait"
      //       slot_level={slot_level}
      //       parent_name={obj.name}
      //       base_name={null}
      //       minion
      //       feature={expanded_feature} 
      //       disabled={disabled}
      //       allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
      //       onChange={(changed: Feature) => {
      //         const new_obj = obj;
      //         new_obj.special_abilities = common_util.replaceInList(new_obj.special_abilities, changed.true_id, changed, "true_id");
      //         onChange(new_obj);
      //         setPing(!ping);
      //       }}
      //       onDelete={() => {
      //         if (expanded_feature) {
      //           const id = expanded_feature.true_id;
      //           const new_obj = obj;
      //           const features = new_obj.special_abilities.filter(o => o.true_id !== id);
      //           new_obj.special_abilities = features;
      //           setExpandedFeature(null);
      //           onChange(new_obj);
      //           setPing(!ping);
      //         }
      //       }}
      //       onDone={() => {
      //         setExpandedFeature(null);
      //       }}
      //     />
      //   );
      // } else {
      //   return (
      //     <FeatureListInput 
      //       label="Trait"
      //       plural="Traits"
      //       features={obj.special_abilities} 
      //       parent_id={obj.true_id} 
      //       parent_type="SummonStatBlock"
      //       disabled={disabled}
      //       onExpand={setExpandedFeature}
      //       onAdd={() => {
      //         const new_obj = obj;
      //         const feature = new Feature();
      //         // feature.parent_type = "SummonStatBlock";
      //         // feature.parent_id = obj.true_id;
      //         new_obj.special_abilities.push(feature);
      //         setExpandedFeature(feature);
      //         onChange(new_obj);
      //         setPing(!ping);
      //       }}
      //     />
      //   );
      // }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <StringBox  
              name="Name"
              value={obj.name} 
              message={obj.name.length > 0 ? "" : "Name Invalid"}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.name = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={12}>
            <StringBox 
              name="Description"
              value={obj.description} 
              multiline
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.description = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>

          <Grid size={3}>
            <SelectStringBox 
              name="Creature Type" 
              options={CREATURE_TYPES}
              value={obj.creature_type} 
              auto_sort
              disabled={disabled}
              onChange={(changed: CreatureType) => {
                const new_obj = obj;
                new_obj.creature_type = changed;
                onChange(new_obj);
                setPing(!ping);
              }} 
            />
          </Grid>
          <Grid size={3}>
            <StringsBox 
              name="Subtype"
              values={obj.subtypes} 
              disabled={disabled}
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.subtypes = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={3}>
            <StringsBox 
              name="Tags"
              values={obj.tags} 
              disabled={disabled}
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.tags = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={3}>
            <CheckBox 
              name="Swarm"
              value={obj.swarm} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.swarm = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>

          <Grid size={4}>
            <UpgradableNumberBox
              name="Hit Dice Count"
              slot_level={slot_level}
              value={obj.hit_dice_count} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.hit_dice_count = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            <CheckBox
              name="HD Size from Creature Size"
              value={obj.hd_size_from_size} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.hd_size_from_size = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            { !obj.hd_size_from_size &&
              <UpgradableNumberBox
                name="Hit Dice Size"
                slot_level={slot_level}
                value={obj.hit_dice_size} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.hit_dice_size = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            }
          </Grid>

          <Grid size={4}>
            <CheckBox
              name="Calc Max HP"
              value={obj.calc_max_hp} 
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.calc_max_hp = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            { !obj.calc_max_hp && 
              <UpgradableNumberBox
                name="Max Hit Points"
                slot_level={slot_level}
                value={obj.max_hit_points} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.max_hit_points = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            }
          </Grid>
          <Grid size={4}>
            <UpgradableNumberBox
              name="Temp Hit Points"
              slot_level={slot_level}
              value={obj.temp_hit_points} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.temp_hit_points = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          
          <Grid size={6}>
            <UpgradableNumberBox
              name="Challenge Rating"
              slot_level={slot_level}
              value={obj.challenge_rating} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.challenge_rating = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={6}>
            <CheckBox
              name="Use Your PB"
              value={obj.use_your_pb} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.use_your_pb = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>

          <Grid size={4}>
            <CheckBox
              name="Share Initiative"
              value={obj.share_initiative} 
              disabled={disabled}
              onChange={(changed) => {
                const new_obj = obj;
                new_obj.share_initiative = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            { !obj.share_initiative &&
              <UpgradableNumberBox
                name="Initiative Modifier"
                slot_level={slot_level}
                value={obj.initiative_modifier} 
                disabled={disabled}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.initiative_modifier = changed;
                  onChange(new_obj);
                  setPing(!ping);
                }}
              />
            }
          </Grid>
          <Grid size={4}>
            <UpgradableNumberBox
              name="Armor Class"
              slot_level={slot_level}
              value={obj.armor_class} 
              disabled={disabled}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.armor_class = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          
          <Grid size={4}>
            <SelectStringBox 
              name="Size"
              options={SIZE_OPTIONS}
              value={`${obj.size_num}`} 
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                const size_num = +changed;
                if (isSizeType(size_num)) {
                  new_obj.size_num = size_num;
                }
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            <SelectStringBox  
              name="Alignment"
              options={[
                "Lawful Good",
                "Lawful Neutral",
                "Lawful Evil",
                "Neutral Good",
                "Neutral",
                "Neutral Evil",
                "Chaotic Good",
                "Chaotic Neutral",
                "Chaotic Evil",
                "Any Alignment",
                "Any Chaotic Alignment",
                "Any Evil Alignment",
                "Any Non-good Alignment",
                "Any Non-lawful Alignment",
                "Unaligned",
                "Neutral Good (50%) Or Neutral Evil (50%)"
              ]}
              value={obj.alignment}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                new_obj.alignment = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            />
          </Grid>
          <Grid size={4}>
            <SelectObjectBox 
              name="Gifts to Automatically Apply"
              data_type="creature_gift"
              values={ obj.built_in_gift_ids }
              disabled={disabled}
              multiple
              display_format='{obj.use_name}'
              onChange={(changed: string[]) => {
                const new_obj = obj;
                new_obj.built_in_gift_ids = changed;
                onChange(new_obj);
                setPing(!ping);
              }}
            /> 
          </Grid>
        </Grid>
      ); 
    }
  }

  const renderActions = () => {
    // console.log(obj);
    if (expanded_feature_base) {
      // Creature FBs are simple, and only ever have one F, and the FB's name and description match the FF.
      const feature = expanded_feature_base.features[0];
      if (feature) {
        // const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
        return (
          <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
            <FeatureBaseInput
              parent_name={obj.name}
              feature_base={expanded_feature_base} 
              feature_bases={obj.action_fbs}
              feature_source={obj}
              disabled={disabled}
              // cfs={creature_instance}
              simple
              allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
              onChange={(changed: FeatureBase) => {
                changed.creature_fb_type = "Actions";
                const new_obj = obj;
                let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
                if (objFinder.length === 1) {
                  const feature_base = objFinder[0];
                  feature_base.copy(changed);
                } else {
                  if (common_util.unique_array(objFinder.map(o => 
                    o.features[0].floating_feature_id)).length === 1) {
                    // Something went wrong, causing it to duplicate one of them.  
                    // Delete them and then add changed.
                    new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                    new_obj.fbs.push(changed);
                  } else {
                    // Give one of them a new ID.  
                    // They probably have different levels, so use that.
                    objFinder = objFinder.filter(o => o.level === changed.level);
                    if (objFinder.length === 1) {
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                      for (const other of others) {
                        other.true_id = uuidv4().toString();
                      }
                    } else {
                      // Just pick one.
                      const feature_base = objFinder[0];
                      feature_base.copy(changed);
                      // Change the ids of the others
                      for (let i = 1; i < objFinder.length; ++i) {
                        const other = objFinder[i];
                        if (other) {
                          other.true_id = uuidv4().toString();
                        }
                      }
                    }
                  }
                }
                onChange(new_obj);
                setPing(!ping);
              }}
              onDelete={() => {
                if (expanded_feature_base) {
                  const id = expanded_feature_base.id;
                  const new_obj = obj;
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                  setExpandedFeatureBase(null);
                  onChange(new_obj);
                  setPing(!ping);
                }
              }}
              onDone={() => {
                setExpandedFeatureBase(null);
              }}
            />
          </div>
        );
      }
      return <>Error</>;
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.action_fbs} 
          feature_source={obj}
          // parent_id={myObj._id} 
          // parent_type="Race"
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          // creature_instances={creature_instances}
          // level_filter={level_filter}
          // onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Actions";
            // new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj.true_id;
            new_fb.feature_source_type = "summon";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            onChange(new_obj);
            setPing(!ping);
            setExpandedFeatureBase(new_fb);
          }}
          onReorder={(updated) => {
            const new_obj = obj;
            for (const u of updated) {
              const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
              if (fb) {
                fb.copy(u);
              }
            }
            // new_obj.fbs = updated;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    }
  }

  // const renderLegendaryActions = () => {
  //   if (expanded_feature_base) {
  //     // const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
  //     return (
  //       <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
  //         <FeatureBaseInput
  //           parent_name={obj.name}
  //           feature_base={expanded_feature_base} 
  //           feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")}
  //           feature_source={obj}
  //           disabled={disabled}
  //           // cfs={creature_instance}
  //           simple
  //           allowed_feature_types={CREATURE_FEATURE_TYPES}
  //           onChange={(changed: FeatureBase) => {
  //             changed.creature_fb_type = "Legendary Actions";
  //             const new_obj = obj;
  //             let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
  //             if (objFinder.length === 1) {
  //               const feature_base = objFinder[0];
  //               feature_base.copy(changed);
  //             } else {
  //               if (common_util.unique_array(objFinder.map(o => 
  //                 o.features[0].floating_feature_id)).length === 1) {
  //                 // Something went wrong, causing it to duplicate one of them.  
  //                 // Delete them and then add changed.
  //                 new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
  //                 new_obj.fbs.push(changed);
  //               } else {
  //                 // Give one of them a new ID.  
  //                 // They probably have different levels, so use that.
  //                 objFinder = objFinder.filter(o => o.level === changed.level);
  //                 if (objFinder.length === 1) {
  //                   const feature_base = objFinder[0];
  //                   feature_base.copy(changed);
  //                   // Change the ids of the others
  //                   const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
  //                   for (const other of others) {
  //                     other.true_id = uuidv4().toString();
  //                   }
  //                 } else {
  //                   // Just pick one.
  //                   const feature_base = objFinder[0];
  //                   feature_base.copy(changed);
  //                   // Change the ids of the others
  //                   for (let i = 1; i < objFinder.length; ++i) {
  //                     const other = objFinder[i];
  //                     if (other) {
  //                       other.true_id = uuidv4().toString();
  //                     }
  //                   }
  //                 }
  //               }
  //             }
  //             onChange(new_obj);
  //             setPing(!ping);
  //           }}
  //           onDelete={() => {
  //             if (expanded_feature_base) {
  //               const id = expanded_feature_base.id;
  //               const new_obj = obj;
  //               new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
  //               onChange(new_obj);
  //               setPing(!ping);
  //               setExpandedFeatureBase(null);
  //             }
  //           }}
  //           onDone={() => {
  //             setExpandedFeatureBase(null);
  //           }}
  //         />
  //       </div>
  //     );
  //   } else {
  //     const cr_options: string[] = [];
  //     for (let cr = obj.min_challenge_rating; cr <= obj.max_challenge_rating; ++cr) {
  //       cr_options.push(`${cr}`);
  //     }
  //     const use_entry = obj.legendaries_table.getEntry(legendaries_cr);
  //     // console.log(use_entry);
  //     const height = window.innerHeight - 64;
  //     return (
  //       <Grid container spacing={0} direction="row">
  //         { (obj.min_challenge_rating === 0 || obj.min_challenge_rating >= 1) && 
  //           (obj.max_challenge_rating === 0 || obj.max_challenge_rating >= 1) && 
  //           <Grid
  //             size={{
  //               xs: 12
  //             }}>
  //             <LegendariesTableInput
  //               obj={obj.legendaries_table}
  //               disabled={disabled}
  //               creature
  //               onChange={(changed: LegendariesTable) => {
  //                 const new_obj = obj;
  //                 new_obj.legendaries_table.copy(changed);
  //                 setObj(new_obj);
  //                 updateCreatureInstances(new_obj);
  //                 setChangeTime(common_util.get_time_number());
  //               }}
  //             />
  //           </Grid>
  //         }
  //         <Grid
  //           size={{
  //             xs: 4
  //           }}>
  //           <SelectStringBox 
  //             name="Select CR for Legendaries"
  //             options={cr_options}
  //             value={`${legendaries_cr}`}
  //             onChange={(changed: string) => {
  //               // debugger;
  //               const new_obj = obj;
  //               const new_cr = +changed;
  //               const old_entry = new_obj.legendaries_table.getEntry(new_cr);
  //               if (!old_entry || old_entry.level !== new_cr) {
  //                 const entry = old_entry ? old_entry.clone() : new Legendaries();
  //                 entry.level = new_cr;
  //                 new_obj.legendaries_table.entries.push(entry);
  //               } else {
  //                 const entry = new Legendaries();
  //                 entry.level = new_cr;
  //                 new_obj.legendaries_table.entries.push(entry);
  //               }
  //               setObj(new_obj);
  //               setChangeTime(common_util.get_time_number());
  //               setLegendariesCR(new_cr);
  //               setReloading(true);
  //             }}
  //           />
  //         </Grid>
  //         <Grid
  //           size={{
  //             xs: 4
  //           }}>
  //           { !reloading &&
  //             <StringBox
  //               name="Legendary Actions"
  //               value={ use_entry ? `${use_entry.actions}` : "0" }
  //               type="number"
  //               onChange={(changed: string) => {
  //                 const new_obj = obj;
  //                 const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
  //                 if (!old_entry) {
  //                   const entry = new Legendaries();
  //                   entry.level = legendaries_cr;
  //                   entry.actions = +changed;
  //                   new_obj.legendaries_table.entries.push(entry);
  //                 } else {
  //                   old_entry.actions = +changed;
  //                 }
  //                 setObj(new_obj);
  //                 setChangeTime(common_util.get_time_number());
  //                 setReloading(true);
  //               }}
  //             /> 
  //           }
  //         </Grid>
  //         <Grid
  //           size={{
  //             xs: 4
  //           }}>
  //           { !reloading &&
  //             <StringBox
  //               name="Legendary Resistances"
  //               value={ use_entry ? `${use_entry.resistances}` : "0" }
  //               type="number"
  //               onChange={(changed: string) => {
  //                 const new_obj = obj;
  //                 const old_entry = new_obj.legendaries_table.entries.find(o => o.level === legendaries_cr);
  //                 if (!old_entry) {
  //                   const entry = new Legendaries();
  //                   entry.level = legendaries_cr;
  //                   entry.resistances = +changed;
  //                   new_obj.legendaries_table.entries.push(entry);
  //                 } else {
  //                   old_entry.resistances = +changed;
  //                 }
  //                 setObj(new_obj);
  //                 setChangeTime(common_util.get_time_number());
  //                 setReloading(true);
  //               }}
  //             />
  //           }
  //         </Grid>
  //         <Grid
  //           size={{
  //             xs: 12
  //           }}>
  //           <FeatureBasesInput 
  //             feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Legendary Actions")} 
  //             feature_source={obj}
  //             // parent_id={myObj._id} 
  //             // parent_type="Race"
  //             disabled={disabled}
  //             onExpand={setExpandedFeatureBase}
  //             creature_instances={creature_instances}
  //             level_filter={level_filter}
  //             onLevelFilterChange={setLevelFilter}
  //             height={height}
  //             onAdd={() => {
  //               const new_obj = obj;
  //               const new_fb = new FeatureBase();
  //               new_fb.creature_fb_type = "Legendary Actions";
  //               new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
  //               new_fb.feature_source_id = new_obj._id;
  //               new_fb.feature_source_type = "creature";
  //               const new_f = new Feature();
  //               new_f.parent_type = new_fb.feature_source_type;
  //               new_f.parent_id = new_fb.feature_source_id;
  //               new_f.base_id = new_fb.id;
  //               new_fb.features.push(new_f);
  //               new_obj.fbs.push(new_fb);
  //               setObj(new_obj);
  //               setExpandedFeatureBase(new_fb);
  //               setChangeTime(common_util.get_time_number());
  //             }}
  //           />
  //         </Grid>
  //       </Grid>
  //     );
  //   }
  // }

  // const renderLairActions = () => {
    
  //   if (expanded_feature_base) {
  //     const creature_instance = creature_instances.find(o => o.challenge_rating === expanded_feature_base.level);
  //     return (
  //       <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
  //         <FeatureBaseInput
  //           parent_name={obj.name}
  //           feature_base={expanded_feature_base} 
  //           feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")}
  //           feature_source={obj}
  //           disabled={disabled}
  //           cfs={creature_instance}
  //           simple
  //           allowed_feature_types={CREATURE_FEATURE_TYPES}
  //           onChange={(changed: FeatureBase) => {
  //             changed.creature_fb_type = "Lair Actions";
  //             const new_obj = obj;
  //             let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
  //             if (objFinder.length === 1) {
  //               const feature_base = objFinder[0];
  //               feature_base.copy(changed);
  //             } else {
  //               if (common_util.unique_array(objFinder.map(o => 
  //                 o.features[0].floating_feature_id)).length === 1) {
  //                 // Something went wrong, causing it to duplicate one of them.  
  //                 // Delete them and then add changed.
  //                 new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
  //                 new_obj.fbs.push(changed);
  //               } else {
  //                 // Give one of them a new ID.  
  //                 // They probably have different levels, so use that.
  //                 objFinder = objFinder.filter(o => o.level === changed.level);
  //                 if (objFinder.length === 1) {
  //                   const feature_base = objFinder[0];
  //                   feature_base.copy(changed);
  //                   // Change the ids of the others
  //                   const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
  //                   for (const other of others) {
  //                     other.true_id = uuidv4().toString();
  //                   }
  //                 } else {
  //                   // Just pick one.
  //                   const feature_base = objFinder[0];
  //                   feature_base.copy(changed);
  //                   // Change the ids of the others
  //                   for (let i = 1; i < objFinder.length; ++i) {
  //                     const other = objFinder[i];
  //                     if (other) {
  //                       other.true_id = uuidv4().toString();
  //                     }
  //                   }
  //                 }
  //               }
  //             }
  //             setObj(new_obj);
  //             setChangeTime(common_util.get_time_number());
  //           }}
  //           onDelete={() => {
  //             if (expanded_feature_base) {
  //               const id = expanded_feature_base.id;
  //               const new_obj = obj;
  //               new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
  //               setObj(new_obj);
  //               setExpandedFeatureBase(null);
  //               setChangeTime(common_util.get_time_number());
  //             }
  //           }}
  //           onDone={() => {
  //             setExpandedFeatureBase(null);
  //           }}
  //         />
  //       </div>
  //     );
  //   } else {
  //     const height = window.innerHeight - 64;
  //     return (
  //       <FeatureBasesInput 
  //         feature_bases={obj.fbs.filter(o => o.creature_fb_type === "Lair Actions")} 
  //         feature_source={obj}
  //         // parent_id={myObj._id} 
  //         // parent_type="Race"
  //         disabled={disabled}
  //         onExpand={setExpandedFeatureBase}
  //         creature_instances={creature_instances}
  //         level_filter={level_filter}
  //         onLevelFilterChange={setLevelFilter}
  //         height={height}
  //         onAdd={() => {
  //           const new_obj = obj;
  //           const new_fb = new FeatureBase();
  //           new_fb.creature_fb_type = "Lair Actions";
  //           new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
  //           new_fb.feature_source_id = new_obj._id;
  //           new_fb.feature_source_type = "creature";
  //           const new_f = new Feature();
  //           new_f.parent_type = new_fb.feature_source_type;
  //           new_f.parent_id = new_fb.feature_source_id;
  //           new_f.base_id = new_fb.id;
  //           new_fb.features.push(new_f);
  //           new_obj.fbs.push(new_fb);
  //           setObj(new_obj);
  //           setExpandedFeatureBase(new_fb);
  //           setChangeTime(common_util.get_time_number());
  //         }}
  //       />
  //     );
  //   }
  // }

  const renderTraits = () => {
    if (expanded_feature_base) {
      return (
        <div style={{ height: "100%", width: "calc(100% - 10px)" }}>
          <FeatureBaseInput
            parent_name={obj.name}
            feature_base={expanded_feature_base} 
            feature_bases={obj.trait_fbs}
            feature_source={obj}
            disabled={disabled}
            simple
            allowed_feature_types={[...CREATURE_FEATURE_TYPES,"Minion Ability"]}
            onChange={(changed: FeatureBase) => {
              changed.creature_fb_type = "Traits";
              const new_obj = obj;
              let objFinder = new_obj.fbs.filter(o => o.id === changed.id);
              if (objFinder.length === 1) {
                const feature_base = objFinder[0];
                feature_base.copy(changed);
              } else {
                if (common_util.unique_array(objFinder.map(o => 
                  o.features[0].floating_feature_id)).length === 1) {
                  // Something went wrong, causing it to duplicate one of them.  
                  // Delete them and then add changed.
                  new_obj.fbs = new_obj.fbs.filter(o => o.id !== changed.id);
                  new_obj.fbs.push(changed);
                } else {
                  // Give one of them a new ID.  
                  // They probably have different levels, so use that.
                  objFinder = objFinder.filter(o => o.level === changed.level);
                  if (objFinder.length === 1) {
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    const others = new_obj.fbs.filter(o => o.id === changed.id && o.level !== changed.level);
                    for (const other of others) {
                      other.true_id = uuidv4().toString();
                    }
                  } else {
                    // Just pick one.
                    const feature_base = objFinder[0];
                    feature_base.copy(changed);
                    // Change the ids of the others
                    for (let i = 1; i < objFinder.length; ++i) {
                      const other = objFinder[i];
                      if (other) {
                        other.true_id = uuidv4().toString();
                      }
                    }
                  }
                }
              }
              onChange(new_obj);
              setPing(!ping);
            }}
            onDelete={() => {
              if (expanded_feature_base) {
                const id = expanded_feature_base.id;
                const new_obj = obj;
                new_obj.fbs = new_obj.fbs.filter(o => o.id !== id);
                onChange(new_obj);
                setPing(!ping);
                setExpandedFeatureBase(null);
              }
            }}
            onDone={() => {
              setExpandedFeatureBase(null);
            }}
          />
        </div>
      );
    } else {
      const height = window.innerHeight - 64;
      return (
        <FeatureBasesInput 
          feature_bases={obj.trait_fbs} 
          feature_source={obj}
          disabled={disabled}
          onExpand={setExpandedFeatureBase}
          // creature_instances={creature_instances}
          // level_filter={level_filter}
          // onLevelFilterChange={setLevelFilter}
          height={height}
          onAdd={() => {
            const new_obj = obj;
            const new_fb = new FeatureBase();
            new_fb.creature_fb_type = "Traits";
            // new_fb.level = level_filter > -1 ? level_filter : new_obj.min_challenge_rating;
            new_fb.feature_source_id = new_obj.true_id;
            new_fb.feature_source_type = "summon";
            const new_f = new Feature();
            new_f.parent_type = new_fb.feature_source_type;
            new_f.parent_id = new_fb.feature_source_id;
            new_f.base_id = new_fb.id;
            new_fb.features.push(new_f);
            new_obj.fbs.push(new_fb);
            onChange(new_obj);
            setPing(!ping);
            setExpandedFeatureBase(new_fb);
          }}
          onReorder={(updated) => {
            const new_obj = obj;
            for (const u of updated) {
              const fb = new_obj.fbs.find(o => o.true_id === u.true_id);
              if (fb) {
                fb.copy(u);
              }
            }
            // new_obj.fbs = updated;
            onChange(new_obj);
            setPing(!ping);
          }}
        />
      );
    }
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    console.log(obj);
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} style={{ width: "100%" }}>
          <TemplateBox
            obj={obj}
            type="SummonStatBlock"
            useTemplate={(template: TemplateBase) => {
              const new_obj = obj;
              // debugger;
              new_obj.copyTemplate(template, api.all_floating_features);
              onChange(new_obj);
              setReloading(true);
            }}
            useCreature={(creature_id: string) => {
              api.getFullObject("creature", creature_id).then(res => {
                if (res && res instanceof Creature) {
                  copyCreature(res).then((new_obj: Summon) => {
                    onChange(new_obj);
                    setReloading(true);
                  });
                }
              });
            }}
          />
        </Grid>
        <Grid size={12}>
          <CenteredMenu
            options={["Home","Attributes","Ability Scores","Actions","Traits"]}
            selected={mode}
            onSelect={setMode}
          />
        </Grid>
        <Grid size={12}
          style={{ 
            overflowY: "scroll", 
            overflowX: "hidden" 
          }}>
            { !reloading && render_tab() }
        </Grid>
      </Grid>
    ); 
  }
}
